import { useEffect, useRef, useState } from "react";
import { AVATAR_ACCEPT, AVATAR_MAX_FILE_BYTES } from "./data";

export function AvatarEditor({
  file,
  onSave,
  onCancel,
}: {
  file: File;
  onSave: (url: string) => void;
  onCancel: () => void;
}) {
  const [source, setSource] = useState(""),
    [zoom, setZoom] = useState(1),
    [x, setX] = useState(50),
    [y, setY] = useState(50),
    [ratio, setRatio] = useState(1),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const processing = useRef(false);
  useEffect(() => {
    generation.current += 1;
    setSource("");
    setError("");
    setBusy(false);
    processing.current = false;
    setZoom(1);
    setX(50);
    setY(50);
    if (
      !AVATAR_ACCEPT.split(",").includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      setError("请选择 10 MB 以内的 JPG、PNG、GIF 或 WebP 图片");
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (!cancelled) {
        setRatio(
          img.naturalWidth / Math.min(img.naturalWidth, img.naturalHeight),
        );
        setSource(url);
      }
    };
    img.onerror = () => {
      if (!cancelled) setError("图片无法读取，请重新选择");
    };
    img.src = url;
    return () => {
      cancelled = true;
      generation.current += 1;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  const save = async () => {
    if (!source || processing.current) return;
    const version = generation.current;
    const current = () => generation.current === version;
    processing.current = true;
    setBusy(true);
    setError("");
    try {
      const bitmap = await createImageBitmap(file);
      try {
        if (!current()) return;
        if (bitmap.width * bitmap.height > 40_000_000)
          throw new Error("图片尺寸过大，请缩小后重试");
        const size = Math.min(bitmap.width, bitmap.height) / zoom;
        const sx = ((bitmap.width - size) * x) / 100,
          sy = ((bitmap.height - size) * y) / 100;
        const canvas = document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 512;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("当前浏览器无法处理图片");
        context.drawImage(bitmap, sx, sy, size, size, 0, 0, 512, 512);
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/webp", 0.85),
        );
        if (!current()) return;
        if (!blob || blob.size > AVATAR_MAX_FILE_BYTES)
          throw new Error("图片压缩失败，请重新选择");
        const result = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            typeof reader.result === "string"
              ? resolve(reader.result)
              : reject(new Error("头像读取失败"));
          reader.onerror = () => reject(new Error("头像读取失败"));
          reader.readAsDataURL(blob);
        });
        if (current()) onSave(result);
      } finally {
        bitmap.close();
      }
    } catch (e) {
      if (current())
        setError(e instanceof Error ? e.message : "图片处理失败，请重新选择");
    } finally {
      if (current()) {
        processing.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <section className="acct-avatar-editor" aria-label="裁剪头像">
      <p>
        裁剪头像{file.type === "image/gif" ? " · GIF 将保存为静态头像" : ""}
      </p>
      {source && (
        <div
          className="acct-crop-preview"
          style={{
            backgroundImage: `url(${source})`,
            backgroundSize: `${zoom * ratio * 100}%`,
            backgroundPosition: `${x}% ${y}%`,
          }}
        />
      )}
      <label>
        缩放
        <input
          type="range"
          min="1"
          max="3"
          step="0.1"
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
      </label>
      <label>
        水平位置
        <input
          type="range"
          min="0"
          max="100"
          value={x}
          onChange={(e) => setX(Number(e.target.value))}
        />
      </label>
      <label>
        垂直位置
        <input
          type="range"
          min="0"
          max="100"
          value={y}
          onChange={(e) => setY(Number(e.target.value))}
        />
      </label>
      {error && (
        <p role="alert" className="auth-error">
          {error}
        </p>
      )}
      <div className="acct-inline-actions">
        <button type="button" className="auth-outline" onClick={onCancel}>
          取消裁剪
        </button>
        <button
          type="button"
          className="auth-primary"
          onClick={() => void save()}
          disabled={!source || busy}
        >
          {busy ? "处理中…" : "使用此头像"}
        </button>
      </div>
    </section>
  );
}
